import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Funnel as FunnelIcon, RefreshCw, TrendingDown, Wallet, Users, MessageSquare,
    Filter, MapPin, Database,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_ICONS = {
    imported: Database,
    opted_in: Users,
    engaged: MessageSquare,
    paid: Wallet,
};

const STAGE_TONE = {
    imported: "border-white/20 bg-white/[0.02]",
    opted_in: "border-electric/40 bg-electric/[0.04]",
    engaged: "border-yellow-300/40 bg-yellow-300/[0.04]",
    paid: "border-emerald-400/50 bg-emerald-400/[0.05]",
};

const TZS = (n) => `TZS ${Number(n || 0).toLocaleString("en-US")}`;

const StageCard = ({ stage, index, total, isFirst }) => {
    const Icon = STAGE_ICONS[stage.key] || Users;
    const width = total > 0 ? Math.max(4, Math.round((stage.count / total) * 100)) : 0;
    return (
        <div className="space-y-2" data-testid={`funnel-stage-${stage.key}`}>
            <div className="flex items-baseline justify-between">
                <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-white/60" />
                    <span className="font-heading text-xs uppercase tracking-[0.18em] text-white/70">{stage.label}</span>
                    {isFirst && <Badge variant="outline" className="text-[9px] uppercase border-white/20 text-white/50 ml-1">Top</Badge>}
                </div>
                {!isFirst && (
                    <div className="flex items-center gap-1.5 text-[11px] text-white/40">
                        <TrendingDown className="h-3 w-3" />
                        {stage.conversion_from_prev}% from previous
                    </div>
                )}
            </div>
            <div className={`relative rounded-sm border ${STAGE_TONE[stage.key] || "border-white/10"} overflow-hidden`}>
                <div
                    className="h-14 transition-all duration-500"
                    style={{
                        width: `${width}%`,
                        background: stage.key === "paid"
                            ? "linear-gradient(90deg, rgba(16,185,129,0.18), rgba(16,185,129,0.04))"
                            : stage.key === "engaged"
                                ? "linear-gradient(90deg, rgba(250,204,21,0.16), rgba(250,204,21,0.03))"
                                : stage.key === "opted_in"
                                    ? "linear-gradient(90deg, rgba(230,0,0,0.20), rgba(230,0,0,0.04))"
                                    : "linear-gradient(90deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02))",
                    }}
                />
                <div className="absolute inset-0 flex items-center justify-between px-4">
                    <div className="font-heading text-2xl font-black text-white tabular-nums">
                        {stage.count.toLocaleString("en-US")}
                    </div>
                    <div className="text-[11px] text-white/40">
                        {stage.conversion_from_top}% of imported
                    </div>
                </div>
            </div>
        </div>
    );
};

const ConversionFunnel = () => {
    const { token } = useAuth();
    const authH = () => ({ Authorization: `Bearer ${token}` });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [region, setRegion] = useState("all");
    const [days, setDays] = useState("all");
    const [regions, setRegions] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (region && region !== "all") params.region = region;
            if (days && days !== "all") params.days = Number(days);
            const r = await axios.get(`${API}/admin/analytics/funnel`, { headers: authH(), params });
            setData(r.data);
        } catch (e) {
            toast.error("Funnel load failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
        }
    }, [region, days, token]); // eslint-disable-line

    const loadRegions = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/admin/customers/regions`, { headers: authH() });
            setRegions(r.data?.regions || []);
        } catch { /* non-fatal */ }
    }, [token]); // eslint-disable-line

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadRegions(); }, [loadRegions]);

    const summary = data?.summary || [];
    const topCount = summary[0]?.count || 0;
    const optIn = summary.find((s) => s.key === "opted_in")?.count || 0;
    const paid = summary.find((s) => s.key === "paid")?.count || 0;

    return (
        <AdminLayout>
            <div className="space-y-6" data-testid="conversion-funnel-page">
                {/* Header */}
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-heading text-3xl font-black text-white flex items-center gap-3">
                            <FunnelIcon className="h-7 w-7 text-electric" />
                            Conversion Funnel
                        </h1>
                        <p className="text-sm text-white/50 mt-1 max-w-2xl">
                            Track the Dimbani acquisition journey: imported phones → opted-in subscribers → engaged chatters → paid customers.
                            Filter by region or customer base to compare campaign performance.
                        </p>
                    </div>
                    <Button
                        onClick={load}
                        disabled={loading}
                        className="bg-electric text-white hover:bg-electric/90"
                        data-testid="funnel-refresh"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </div>

                {/* Filters */}
                <Card className="bg-paper border-border p-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <Filter className="h-4 w-4 text-white/40" />
                        <div className="flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-white/40" />
                            <Select value={region} onValueChange={setRegion}>
                                <SelectTrigger className="w-48 bg-obsidian border-border text-white" data-testid="funnel-region-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All regions</SelectItem>
                                    {regions.map((r) => (
                                        <SelectItem key={r.region} value={r.region}>
                                            {r.region} ({r.count})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Select value={days} onValueChange={setDays}>
                            <SelectTrigger className="w-48 bg-obsidian border-border text-white" data-testid="funnel-window-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All time</SelectItem>
                                <SelectItem value="7">Last 7 days</SelectItem>
                                <SelectItem value="30">Last 30 days</SelectItem>
                                <SelectItem value="90">Last 90 days</SelectItem>
                            </SelectContent>
                        </Select>
                        {(region !== "all" || days !== "all") && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setRegion("all"); setDays("all"); }}
                                className="text-white/60 hover:text-white"
                                data-testid="funnel-clear-filters"
                            >
                                Clear filters
                            </Button>
                        )}
                    </div>
                </Card>

                {/* Top-line metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-paper border-border p-5">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading">Top of Funnel</div>
                        <div className="mt-2 text-3xl font-heading font-black text-white">{topCount.toLocaleString("en-US")}</div>
                        <div className="text-xs text-white/40 mt-1">Imported phones</div>
                    </Card>
                    <Card className="bg-paper border-border p-5">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading">Opt-In Rate</div>
                        <div className="mt-2 text-3xl font-heading font-black text-electric">
                            {topCount ? Math.round((optIn / topCount) * 100) : 0}%
                        </div>
                        <div className="text-xs text-white/40 mt-1">{optIn.toLocaleString("en-US")} of {topCount.toLocaleString("en-US")}</div>
                    </Card>
                    <Card className="bg-paper border-border p-5">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading">Paid Conversion</div>
                        <div className="mt-2 text-3xl font-heading font-black text-emerald-300">
                            {topCount ? ((paid / topCount) * 100).toFixed(1) : 0}%
                        </div>
                        <div className="text-xs text-white/40 mt-1">{paid.toLocaleString("en-US")} paying customers</div>
                    </Card>
                    <Card className="bg-paper border-border p-5">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading">Revenue</div>
                        <div className="mt-2 text-3xl font-heading font-black text-white tabular-nums">
                            {TZS(data?.revenue?.total)}
                        </div>
                        <div className="text-xs text-white/40 mt-1">
                            Avg {TZS(data?.revenue?.avg_per_paid_customer)}/customer
                        </div>
                    </Card>
                </div>

                {/* Funnel stages */}
                <Card className="bg-paper border-border p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <h2 className="font-heading text-lg font-bold text-white">Funnel Stages</h2>
                        <Badge variant="outline" className="text-[10px] uppercase border-white/15 text-white/50">
                            {region !== "all" ? region : "All Regions"} · {days !== "all" ? `${days}d window` : "All time"}
                        </Badge>
                    </div>
                    <div className="space-y-5">
                        {summary.map((s, i) => (
                            <StageCard key={s.key} stage={s} index={i} total={topCount} isFirst={i === 0} />
                        ))}
                    </div>
                </Card>

                {/* By Region & By Base breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <Card className="bg-paper border-border p-5">
                        <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-electric" />
                            By Region
                        </h3>
                        {(data?.by_region || []).length === 0 ? (
                            <div className="text-sm text-white/40 py-8 text-center">No regional breakdown yet</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="funnel-by-region-table">
                                    <thead>
                                        <tr className="text-[10px] uppercase tracking-wider text-white/40 border-b border-border">
                                            <th className="text-left py-2 font-heading">Region</th>
                                            <th className="text-right py-2 font-heading">Imp</th>
                                            <th className="text-right py-2 font-heading">Opt-in</th>
                                            <th className="text-right py-2 font-heading">Eng</th>
                                            <th className="text-right py-2 font-heading">Paid</th>
                                            <th className="text-right py-2 font-heading">Imp→Opt</th>
                                            <th className="text-right py-2 font-heading">Opt→Pd</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data?.by_region || []).slice(0, 15).map((r) => (
                                            <tr key={r.region} className="border-b border-border/40 hover:bg-white/[0.02]">
                                                <td className="py-2.5 text-white">{r.region}</td>
                                                <td className="py-2.5 text-right text-white/80 tabular-nums">{r.imported}</td>
                                                <td className="py-2.5 text-right text-electric tabular-nums">{r.opted_in}</td>
                                                <td className="py-2.5 text-right text-yellow-300 tabular-nums">{r.engaged}</td>
                                                <td className="py-2.5 text-right text-emerald-300 tabular-nums">{r.paid}</td>
                                                <td className="py-2.5 text-right text-white/60 tabular-nums">{r.conv_imp_to_opt}%</td>
                                                <td className="py-2.5 text-right text-white/60 tabular-nums">{r.conv_opt_to_paid}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>

                    <Card className="bg-paper border-border p-5">
                        <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                            <Database className="h-4 w-4 text-electric" />
                            By Customer Base
                        </h3>
                        {(data?.by_base || []).length === 0 ? (
                            <div className="text-sm text-white/40 py-8 text-center">No base breakdown yet — upload a CSV to start tracking.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="funnel-by-base-table">
                                    <thead>
                                        <tr className="text-[10px] uppercase tracking-wider text-white/40 border-b border-border">
                                            <th className="text-left py-2 font-heading">Base</th>
                                            <th className="text-right py-2 font-heading">Imp</th>
                                            <th className="text-right py-2 font-heading">Opt-in</th>
                                            <th className="text-right py-2 font-heading">Eng</th>
                                            <th className="text-right py-2 font-heading">Paid</th>
                                            <th className="text-right py-2 font-heading">Imp→Opt</th>
                                            <th className="text-right py-2 font-heading">Opt→Pd</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data?.by_base || []).slice(0, 15).map((r) => (
                                            <tr key={r.base} className="border-b border-border/40 hover:bg-white/[0.02]">
                                                <td className="py-2.5 text-white truncate max-w-[180px]" title={r.base}>{r.base}</td>
                                                <td className="py-2.5 text-right text-white/80 tabular-nums">{r.imported}</td>
                                                <td className="py-2.5 text-right text-electric tabular-nums">{r.opted_in}</td>
                                                <td className="py-2.5 text-right text-yellow-300 tabular-nums">{r.engaged}</td>
                                                <td className="py-2.5 text-right text-emerald-300 tabular-nums">{r.paid}</td>
                                                <td className="py-2.5 text-right text-white/60 tabular-nums">{r.conv_imp_to_opt}%</td>
                                                <td className="py-2.5 text-right text-white/60 tabular-nums">{r.conv_opt_to_paid}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </AdminLayout>
    );
};

export default ConversionFunnel;
